/**
 * **Coverage + the two-column gap report** — the
 * honest, read-only analysis over an overlay that says, per authored *clause*, how fully
 * the engine mechanizes it, and lists the gaps in two columns. **The gap report is the
 * authoring backlog's input.**
 *
 * **Read-only — it changes no resolution behaviour.** Coverage is a static walk over the
 * overlay entries + the calibrated vocabularies (the implemented atom ops, predicate ops,
 * modifier facets, trigger points). It never resolves anything, never emits a step, never
 * touches `applyResolved`. The vocabulary it checks against is *introspected*, not
 * hardcoded per-entity, so the same analysis runs over a small seed set or the whole
 * compendium with no change.
 *
 * **The per-clause tier.** Each {@link EffectEntry} on an entry is one *clause*. A
 * clause is classified into one of three tiers:
 *   - **`mechanized`** — every atom op / predicate op / modifier facet the clause uses is
 *     in the implemented vocabulary AND maps onto the grounded step set (or folds into the
 *     stat/roll math). The data path runs it end-to-end, mech-vs-mech.
 *   - **`advisory`** — runs partially: the clause references a facet the engine *surfaces
 *     but does not fully mechanize* — a `gm_ruling` predicate (a GM ruling is in the loop), an
 *     `advisory` atom (a GM-routed note the GM still rules on), a declared-but-deferred
 *     rider (a tag rider in {@link UNMET_STEP_DEMANDS}, Prone's spatial rider), or the
 *     empty `builtin` hatch. The clause still emits its grounded steps; the deferred
 *     facet routes to the GM and is *reported* (the resolver pushes these onto
 *     `InterpretResult.warnings` at run time — coverage is the static mirror of that
 *     surfacing).
 *   - **`manual`** — the narrative tier: a clause the engine cannot mechanize at
 *     all because it uses an atom op / predicate op that is **unimplemented** (the cheap
 *     gap column) or a facet that needs a **new grounded step** (the expensive gap column).
 *     It routes entirely to the GM and is reported — never silently dropped.
 *
 * **The two-column gap report.** The aggregate over an overlay lists
 * the demand the corpus places on the vocabulary, split by *cost*:
 *   - **unmet atoms** (the cheap column) — an atom op or predicate op referenced by an
 *     authored clause but not yet in {@link IMPLEMENTED_ATOM_OPS}/{@link
 *     IMPLEMENTED_PREDICATE_OPS}. Closing one is a new `case` in the interpreter/evaluator.
 *   - **unmet steps** (the expensive column) — a facet that needs a genuinely new grounded
 *     primitive in `domain/encounter/steps.ts` with its inverse + round-trip test. A clause
 *     that only *defers* such a primitive (a tag rider named in {@link UNMET_STEP_DEMANDS}) is
 *     `advisory` and names the step it's waiting on here.
 *
 * Both columns are a typed structure (`CoverageReport`), queryable + serializable (the
 * `lvtt overlay coverage` surface renders it; tooling consumes it as JSON). Never
 * console noise.
 */

import type {
  EffectAtom,
  EffectEntry,
  ModifierContribution,
  OverlayEntry,
} from "./atoms";
import type { Predicate } from "./predicate";
import type { TriggerFamily } from "./family";
import { SCHEMA_PREDICATE_OPS } from "./schema";

// ── the calibrated, implemented vocabularies (the source of truth for "mechanized") ──

/**
 * The atom ops the interpreter (`interpret.ts`) has a `case` for — the cheap column's
 * yardstick. An authored atom whose `op` is NOT here is an **unmet atom**: closing the
 * gap is a new arm in {@link EffectAtom} + a `case` in `interpretAtom`. Keep this in lock-
 * step with `interpretAtom`'s switch (a `coverage.test.ts` fixture asserts they match).
 */
export const IMPLEMENTED_ATOM_OPS = [
  "add_status",
  // Plant a weapon-pinned follow-up reservation on the attacker (Executioner's Backswing
  // Cut: same weapon instance + excluded target, cashed free + half-damage at the room gate).
  "reserve_followup",
  "remove_status",
  // The source-scoped CROSS-TARGET sibling of `remove_status` (single-slot mark replace
  // across DIFFERENT targets, `t_heavy_gunner:1`'s Covering Fire).
  "remove_status_from_source",
  // Suspend/resume a status instance's expiry timer (ms_stay_of_execution's "any active
  // effects on your target are frozen in time").
  "pause_status_expiry",
  "add_resource",
  "set_resource",
  // t_executioner:1's on-hit "additional Skirmish/Barrage action".
  "grant_action_allowance",
  "grant_free_action_use",
  "extra_damage",
  "add_heat",
  "set_hp",
  // Grow the acting unit's move budget (Ace's Afterburners).
  "grant_move",
  // Expend charges from a held Limited pool, settled as a cost event (Walking Armory's Ammo Case).
  "expend_charges",
  // Spend a cadence cap, per target when it names one (Stasis Generator, Eject Power Cores).
  "use_cap",
  // Keep-highest Overshield and a Burn clear (Black Thumb's Field Repair and Extinguish).
  "overshield",
  "clear_burn",
  // Start and end a Black Thumb Rodeo (`rodeo.ts`).
  "begin_rodeo",
  "end_rodeo",
  "reveal",
  // `amount` is a `MagnitudeValue` — a plain number OR a stat-scaled
  // `{ stat, who?, mult?, add? }` (pg_patch's half-max-HP heal).
  "heal",
  // `npc-features-fielded-roster` Phase 5.2 — a standalone, mitigated, cascade-integrated hit
  // for a clause firing outside any attack resolution (Trample's `on:"trample"`).
  "damage",
  "reduce_pending_damage",
  "split_pending_damage",
  "redirect_pending_damage",
  // `npc-features-fielded-roster` Phase 7.1 — NOT pre-extracted; composes under `if`/`roll`.
  "resist_pending_damage",
  "forced_move",
  // t_hunter:1's Lunge / t_ace:1's any-direction Acrobatics.
  "move_self_toward_target",
  // Guide the Flock: a board-cell pick within a movement distance, one prompt per mover.
  "move_up_to",
  // Content-driven deploy onto the existing `spawnObjectGroup`
  // helper (ms_pattern_a_jericho_deployable_cover's cover sections).
  "deploy_object",
  // Deploy a plain mine Combatant via the existing
  // `spawnMine` engine adapter (HEX Charges' Explosive Mine, Smoke Charges' Smoke Mine).
  "deploy_mine",
  // The inverse of `deploy_object` — recall a deployed group back into the system, freeing
  // it for redeploy.
  "recall_object",
  // Move a deployed drone to a new space by the deploy's placement rule.
  "redeploy_object",
  // Take a deployed drone off the field without destroying it (Restock's disintegration).
  "despawn",
  // Reload one of the target's Loading weapons (Restock).
  "reload",
  // Spend a Repair, scoped to the carrying system, to restore a deployed group's Destroyed
  // sections.
  "repair_object_group",
  // Mint a standing AreaEffect (a thrown Smoke Grenade's Blast cloud), splicing an
  // `area_add` step. Never stamps a status — the created record is later folded into cover
  // as a live positional query.
  "create_area",
  "force_save",
  "skill_check",
  "roll",
  "if",
  "choose",
  "for_each",
  "choose_random",
  "choose_from_area",
  "place_adjacent",
  // Teleport to a picked free space (Fold Knife, Blink Charges): a `forced_move` step flagged `teleport`.
  "teleport",
  // "You may move N spaces" off the mover's own move, picked on the board (Lockbreaker, Reactive Weave).
  "move_spaces",
  // Mark one of the carrier's own systems destroyed (External Batteries).
  "destroy_system",
  "forced_attack",
  // `ll1-granted-reactions` S4 — Stasis Bolt's contested roll, and the declared window's miss.
  "contested_roll",
  "cause_miss",
  "builtin",
  // Surface an authored GM-facing warning verbatim (Impale's un-automated pull-to-endpoint —
  // `npc-features-fielded-roster` Phase 6.2).
  "advisory",
] as const satisfies readonly EffectAtom["op"][];

/**
 * The predicate ops the evaluator (`predicate.ts`) has a `case` for — the other half of
 * the cheap column. An authored predicate whose `op` is NOT here is an **unmet atom** (a
 * new arm in {@link Predicate} + a `case` in `evaluatePredicate`). The `gm_ruling` op IS
 * implemented, but a clause *using* it is `advisory`: a GM ruling is in the loop.
 *
 * Not a hand list: it is the schema's own op list ({@link SCHEMA_PREDICATE_OPS}, read off the
 * zod union), whose arms carry the per-op comments. A schema arm lands together with its
 * {@link Predicate} union arm, and `evaluatePredicate`'s exhaustive `switch` makes the union arm
 * a tsc error until it has a case.
 */
export const IMPLEMENTED_PREDICATE_OPS: readonly Predicate["op"][] = SCHEMA_PREDICATE_OPS;

/**
 * Modifier facets that fully fold into the stat/roll/damage math today (`modifiers.ts`).
 * A `modifier` clause using only these is fully mechanized; a `tag` rider is classified by
 * {@link RECORDED_TAG_RIDERS} / {@link UNMET_STEP_DEMANDS}.
 */
export const FOLDED_MODIFIER_FACETS = [
  "attackerAccuracy",
  "targetAccuracy",
  "consumeOnHit",
  "ignoreTargetArmor",
  "doubleDamage",
  "immunity",
  "resistance",
  // Stunned's Evasion cap + auto-fail HULL/AGI: both fold straight into the stat/roll math
  // (the defense read, the save/contest roll sites) exactly like the facets above — no
  // new atom/predicate op, so a clause carrying only these (plus an `advisory` string)
  // stays `mechanized` under `classifyClause`, which never special-cases a bare
  // `advisory` field.
  "evasionCap",
  "autoFailChecks",
  // The shared "1/round" cap facet folds straight into the gate `foldModifiers` already
  // runs (no new atom/predicate op), so a modifier clause carrying only this (plus the
  // facets above) stays `mechanized`.
  "consumeResourceOnApply",
  // Walking Armory's SABOT: the attack's damage is AP.
  "armorPiercing",
  // Walking Armory's HELLFIRE: the attack's bonus damage takes its type.
  "bonusDamageType",
  // t_walking_armory:1's per-attack damage-type override.
  "overrideDamageType",
  // The save-scope facet (cause and/or stat; ms_stable_structure's cause-scoped boon and
  // t_ace:1's stat-scoped one both ride this single facet).
  "saveScope",
  // t_vanguard:2's ignore-cover.
  "ignoreCover",
] as const satisfies readonly (keyof ModifierContribution)[];

/**
 * Tag-rider names whose `modifier` clause carries its bound `{VAL}` into the step list as a
 * recorded rider (auditable/replayable), where the *modifier* clause itself emits no step —
 * the implied rule is satisfied elsewhere. For AP/Overkill/Reliable/Limited the mechanism is
 * in the frozen profile. For **Knockback** the positional push is
 * a *sibling `triggered` clause* emitting the `forced_move` grounded step: the tag-rider
 * modifier is the recorded provenance, and the
 * push is mechanized by the on-hit clause — so Knockback names no unmet step.
 * A `modifier` whose `tag.name` is here is `mechanized`: the clause records the rider and the
 * engine runs the mechanism. A name in {@link UNMET_STEP_DEMANDS} is `advisory` instead: it
 * defers a new grounded step.
 * **Loading/Smart/selfHeat/Ordnance** are frozen-profile mechanics
 * (`WeaponProfile.loading`/`.smart`/`.selfHeat`/`.ordnance`, applied by
 * the `checkLoaded` gate / the E-Defense swap / the fire-heat cascade / the three room-level
 * ordnance gates in engine code), so their authored entries are recorded riders in the AP lane —
 * the mechanism never reads the overlay.
 * **Thrown**'s reach extension and cover-fold mechanism ride the frozen `WeaponProfile.tags`
 * array inline
 * (`checkAttackRange` in economy.ts, the `forcesCover` check in resolve/attackModifiers.ts — same idiom as
 * `tg_seeking`'s inline exemption), not the overlay's `mod.tag.val` — so `tg_thrown`'s
 * authored entry is a recorded rider too.
 */
export const RECORDED_TAG_RIDERS = ["ap", "overkill", "reliable", "limited", "knockback", "loading", "smart", "selfHeat", "ordnance", "thrown"] as const;

/**
 * The map from a *deferred facet* the corpus uses to the **new grounded step** it would
 * need (the expensive gap column). A clause that touches one of these facets
 * is `advisory` (it defers honestly), and the facet is tallied in `CoverageReport.unmetSteps`
 * so the engine-work backlog stays distinct from the cheap atom backlog. This
 * is intentionally a *small, named* set — a genuinely new demand the corpus surfaces gets
 * added here with the step it implies, never invented silently.
 *
 * **Currently empty**: every clause folds into the calibrated step set. The map stays here
 * (typed, ready) for the next genuinely-new positional/resistance primitive the corpus
 * surfaces.
 */
export const UNMET_STEP_DEMANDS: Record<string, { step: string; why: string }> = {};

// ── the typed coverage structures (the queryable artifact) ───────────────────────────

/** The three coverage tiers a clause lands in. */
export type CoverageTier = "mechanized" | "advisory" | "manual";

/**
 * One **unmet atom** (the cheap gap column): an atom op or predicate op an authored clause
 * referenced that the interpreter/evaluator does not implement. `kind` says which vocabulary
 * (a new interpreter `case` vs a new evaluator `case`). `count` is how many clauses demand it
 * (the backlog prioritizes by demand). `entries` lists the entity ids that surfaced it.
 */
export type UnmetAtom = {
  kind: "atom" | "predicate";
  op: string;
  count: number;
  entries: string[];
}

/**
 * One **unmet step** (the expensive gap column): a deferred facet that needs a genuinely new
 * grounded primitive in `domain/encounter/steps.ts`. `step` is the proposed step op; `why`
 * is the one-line rationale; `count`/`entries` are the demand. This is the column that costs
 * real engine work (a new step + its inverse + a round-trip test), so it is a different
 * backlog than the cheap atom additions.
 */
export type UnmetStep = {
  facet: string;
  step: string;
  why: string;
  count: number;
  entries: string[];
}

/**
 * One **implemented** op's usage demand across the analysed corpus. Distinct from
 * {@link UnmetAtom}: that column tracks ops the corpus references but the engine does NOT
 * implement; this tracks the OPPOSITE direction — every op the engine DOES implement
 * ({@link IMPLEMENTED_ATOM_OPS} / {@link IMPLEMENTED_PREDICATE_OPS}), by how many clauses
 * actually reference it. `count === 0` is the headline: a carrier-less op like
 * `has_capability`, or a pack-untouched one like `pause_status_expiry`/`repair_object_group`,
 * self-announces here instead of being indistinguishable from a heavily-used one.
 */
export type OpUsage = {
  kind: "atom" | "predicate";
  op: string;
  count: number;
}

/**
 * The per-clause coverage classification — the row the gap report rolls up from. One per
 * {@link EffectEntry} on an overlay entry. `tier` is the headline; `reasons` is why it
 * landed there (the advisory deferrals / the unmet ops it triggered) — the honest surfacing,
 * never a silent cap.
 */
export type ClauseCoverage = {
  /** The overlay entry id this clause belongs to (`mw_…`, a status name, a `tg_…`). */
  entry: string;
  /** The clause's index within the entry's `effects[]` (stable address). */
  clause: number;
  /** The clause's activation kind (`modifier`/`triggered`/`action`/`select`). */
  kind: EffectEntry["kind"];
  tier: CoverageTier;
  /** Human-readable notes: the advisory deferrals + unmet ops/steps that set the tier. */
  reasons: string[];
}

/**
 * The aggregate coverage report over an overlay — **the load-bearing deliverable**.
 * `tierCounts` is the headline tally; `clauses` is the per-clause detail; `unmetAtoms` /
 * `unmetSteps` are the two gap columns that form the authoring backlog.
 * Fully serializable (the `lvtt overlay coverage --json` surface emits it verbatim).
 */
export type CoverageReport = {
  /** Total overlay entries analysed. */
  entries: number;
  /** Total clauses (effect entries) across all overlay entries. */
  clauses: number;
  /** The per-tier clause tally (mechanized / advisory / manual). */
  tierCounts: Record<CoverageTier, number>;
  /** Every clause's classification (the detail the tally rolls up). */
  perClause: ClauseCoverage[];
  /** The cheap gap column: atom/predicate ops referenced but unimplemented, by demand. */
  unmetAtoms: UnmetAtom[];
  /** The expensive gap column: deferred facets needing a new grounded step, by demand. */
  unmetSteps: UnmetStep[];
  /**
   * Per-op usage tally over the WHOLE implemented
   * vocabulary ({@link IMPLEMENTED_ATOM_OPS} ∪ {@link IMPLEMENTED_PREDICATE_OPS}), each with its
   * real reference count across `entries` — `0` self-announces a zero-carrier op instead of
   * silently reading as "covered." Complements {@link unmetAtoms} (which only covers the
   * UNimplemented direction) rather than replacing it.
   */
  opUsage: OpUsage[];
}

// ── walking the recipe tree (atoms + predicates) ─────────────────────────────────────

/** Visit every atom in an atom list, recursing into control-flow branches. */
function walkAtoms(atoms: readonly EffectAtom[], visit: (a: EffectAtom) => void): void {
  for (const a of atoms) {
    visit(a);
    switch (a.op) {
      case "force_save":
      case "skill_check":
      case "deploy_mine":
        if (a.on_fail) walkAtoms(a.on_fail, visit);
        if (a.on_pass) walkAtoms(a.on_pass, visit);
        break;
      case "roll":
        if (a.on_hit) walkAtoms(a.on_hit, visit);
        if (a.on_miss) walkAtoms(a.on_miss, visit);
        break;
      case "contested_roll":
        if (a.on_win) walkAtoms(a.on_win, visit);
        if (a.on_lose) walkAtoms(a.on_lose, visit);
        break;
      case "if":
        walkAtoms(a.then, visit);
        if (a.else) walkAtoms(a.else, visit);
        break;
      case "choose":
        for (const o of a.options) walkAtoms(o.effect, visit);
        break;
      case "for_each":
      case "choose_random":
      case "choose_from_area":
        walkAtoms(a.effect, visit);
        break;
    }
  }
}

/** Collect every atom op used in an atom list (recursing into control-flow branches). */
function collectAtomOps(atoms: readonly EffectAtom[], out: Set<string>): void {
  walkAtoms(atoms, (a) => out.add(a.op));
}

/** Every `advisory` atom's message in an atom list (recursing into control-flow branches). */
function advisoryMessages(atoms: readonly EffectAtom[]): string[] {
  const out: string[] = [];
  walkAtoms(atoms, (a) => {
    if (a.op === "advisory") out.push(a.message);
  });
  return out;
}

/** Collect every predicate op used in an atom list's conditions (recursing into branches + composites). */
function collectPredicateOps(atoms: readonly EffectAtom[], out: Set<string>): void {
  for (const a of atoms) {
    switch (a.op) {
      case "if":
        collectPredOps(a.cond, out);
        collectPredicateOps(a.then, out);
        if (a.else) collectPredicateOps(a.else, out);
        break;
      case "force_save":
      case "skill_check":
      case "deploy_mine":
        if (a.on_fail) collectPredicateOps(a.on_fail, out);
        if (a.on_pass) collectPredicateOps(a.on_pass, out);
        break;
      case "roll":
        if (a.on_hit) collectPredicateOps(a.on_hit, out);
        if (a.on_miss) collectPredicateOps(a.on_miss, out);
        break;
      case "contested_roll":
        if (a.on_win) collectPredicateOps(a.on_win, out);
        if (a.on_lose) collectPredicateOps(a.on_lose, out);
        break;
      case "choose":
        for (const o of a.options) collectPredicateOps(o.effect, out);
        break;
      case "for_each":
      case "choose_random":
      case "choose_from_area":
        if (a.in.where) collectPredOps(a.in.where, out);
        collectPredicateOps(a.effect, out);
        break;
    }
  }
}

/** Collect predicate ops from a single predicate tree (recursing composites). */
function collectPredOps(pred: Predicate, out: Set<string>): void {
  out.add(pred.op);
  switch (pred.op) {
    case "and":
    case "or":
      for (const p of pred.preds) collectPredOps(p, out);
      break;
    case "not":
      collectPredOps(pred.pred, out);
      break;
  }
}

/** Whether a `gm_ruling` predicate appears in an atom list (drives the `advisory` tier). */
function usesGmRuling(atoms: readonly EffectAtom[]): boolean {
  const ops = new Set<string>();
  collectPredicateOps(atoms, ops);
  return ops.has("gm_ruling");
}

/** Whether a predicate tree contains a `gm_ruling` predicate. */
function predUsesGmRuling(pred: Predicate): boolean {
  const ops = new Set<string>();
  collectPredOps(pred, ops);
  return ops.has("gm_ruling");
}

const IMPL_ATOM = new Set<string>(IMPLEMENTED_ATOM_OPS);
const IMPL_PRED = new Set<string>(IMPLEMENTED_PREDICATE_OPS);
const FOLDED_FACETS = new Set<string>(FOLDED_MODIFIER_FACETS);
const TAG_RIDER = new Set<string>(RECORDED_TAG_RIDERS);

// ── carrier-kind reachability ────────────────────────────────────────────────────────

/**
 * The carrier-entity kinds this reachability check can **confidently** infer from an overlay
 * entry's OWN id, by the pack's established naming convention
 * (`packs/core-rulebook/AUTHORING-PLAYBOOK.md`): `ms_*` = a mech SYSTEM, `mw_*` = a WEAPON,
 * `tg_*` = a weapon TAG template, `t_<slug>:<rank>` = a TALENT rank, `cb_*` = a CORE BONUS,
 * `pg_*` = a PILOT-GEAR item.
 */
export type ReachabilityKind = "system" | "weapon" | "tag" | "talent" | "core_bonus" | "pilot_gear" | "npc_feature";

/**
 * Infer an overlay entry's {@link ReachabilityKind} from its id — a pure, static naming-
 * convention read, NEVER a guess: every OTHER id shape (a bare status/action display name
 * like `"Lock On"`/`"Argonaut Shield"`, a frame-effect id like `everest_initiative`) returns
 * `undefined`, deliberately UNCLASSIFIED rather than misclassified. `undefined` means "this
 * reachability check has nothing to say about this entry" — it can only ever ADD honesty to
 * `classifyClause`, never invent a false demotion off a bad guess (status-borne content, in
 * particular, already gathers at nearly every point via {@link gatherStatusHandlers}'s
 * point-agnostic scan, so leaving it unclassified costs nothing and risking a wrong
 * classification there would).
 *
 * **`pg_*` (pilot gear) is classified** because `CombatantBase.pilotGearEffectIds` is a real
 * gather vector (riding `gatherPilotHandlers` alongside `pilotEffectIds`/`frameEffectIds`), so
 * it gets its own kind, checked the same way a `talent`/`core_bonus` id is (see {@link
 * verifyEntryCarrier}'s `carriesAsPilotEffect`).
 *
 * **`npcf_*` (NPC feature) is classified** for the same reason, as of Gap A
 * (`npc-features-fielded-roster` Phase 1): `CombatantBase.npcFeatureEffectIds` is a real gather
 * vector for every merged NPC feature whose `type` is neither `Weapon` nor `System` — it rides
 * `gatherPilotHandlers` merged in by `combatantEffectIds`, exactly like `pilotGearEffectIds`.
 * Before this, an `npcf_*` overlay entry had no arm here at all and `overlay validate` was
 * silent on it regardless of type (`docs/agents/memories/pack-authoring.md` "NPC overlay
 * entries: the carrier type gate"). A `npcf_*` id whose feature is actually `type: "System"`
 * still classifies here as `npc_feature` by this naming check alone — that's fine, because
 * `REACHABLE_TRIGGER_POINTS.npc_feature`/`REACHABLE_MODIFIER_FACETS.npc_feature` are the SAME
 * row as `system`'s (both ride a real vector reaching the same fire/fold sites), so the
 * reachability *verdict* doesn't depend on getting the exact carrier sub-kind right here.
 */
export function reachabilityKindOf(entryId: string): ReachabilityKind | undefined {
  if (entryId.startsWith("ms_")) return "system";
  if (entryId.startsWith("mw_")) return "weapon";
  if (entryId.startsWith("tg_")) return "tag";
  if (entryId.startsWith("cb_")) return "core_bonus";
  if (entryId.startsWith("pg_")) return "pilot_gear";
  if (entryId.startsWith("npcf_")) return "npc_feature";
  if (/^t_[a-z0-9_]+:\d+$/.test(entryId)) return "talent";
  return undefined;
}

/**
 * The {@link TriggerFamily}s a `triggered` clause carried by each {@link ReachabilityKind} can
 * ACTUALLY fire at — the static mirror of the real gather-site wiring
 * (`loader.ts`'s `gatherHandlers`/`gatherPilotHandlers`/`gatherSystemHandlers`, and their call
 * sites in the `resolve/` modules and `interpret.ts`). **This is a hand-maintained calibration, kept in
 * lock-step with that wiring** — mirroring {@link IMPLEMENTED_ATOM_OPS}'s own "keep this in
 * sync with the interpreter's switch" discipline. A new fire site must add its kind here too,
 * or a real reachable clause at that point would falsely read as unreachable (`manual`).
 *
 * `weapon`/`tag` — `gatherHandlers`'s 4 wired points (`fireAttackDeclared`, on_hit/on_crit,
 * on_miss), all inside the one `"attack"` family. `talent`/`core_bonus`/`pilot_gear` —
 * `gatherPilotHandlers`'s wired points (every
 * fired point except `on_status_lost`, which gathers off a by-name/status-only seam —
 * `gatherHandlersForLostStatus`); `pilot_gear`'s ids
 * ride the SAME `gatherPilotHandlers` call as `talent`/`core_bonus` (merged in by
 * `combatantEffectIds`), so it reaches every point they do. `system` — `gatherSystemHandlers`'s
 * wired points, the SAME set as pilot handlers. `on_action_taken` is in the
 * talent/core_bonus/pilot_gear/system set — `resolveActionTakenTrigger` (`resolve.ts`) gathers
 * off the SAME three vectors (`gatherStatusHandlers`/`gatherPilotHandlers`/
 * `gatherSystemHandlers`) `resolveLifecycleTrigger` does, never a weapon/tag one (no firing
 * weapon is in scope at an action-commit moment).
 */
export const REACHABLE_TRIGGER_POINTS: Record<ReachabilityKind, readonly TriggerFamily[]> = {
  weapon: ["attack"],
  tag: ["attack"],
  // `"damage"` (`decisions/0175`): `resolveDamageTrigger` gathers the pilot and system vectors
  // for NON-attack damage (the attack window's `compileAutomaticOutcome` reaches pilot too, since
  // `decisions/0166`).
  // `"move"`: `resolveMoveTrigger` gathers the pilot vector (Panopticon's marker clear).
  talent: ["attack", "save", "start_turn", "end_turn", "start_round", "end_round", "action", "damage", "move"],
  core_bonus: ["attack", "save", "start_turn", "end_turn", "start_round", "end_round", "action", "damage"],
  pilot_gear: ["attack", "save", "start_turn", "end_turn", "start_round", "end_round", "action", "damage"],
  // `"aura"`: `resolveAuraTrigger` gathers the deploying system's entry off a drone's `deployedFrom`
  // (`gatherAuraHandlers`), the only carrier kind that deploys a drone.
  system: ["attack", "save", "start_turn", "end_turn", "start_round", "end_round", "action", "damage", "aura"],
  // Gap A (`npc-features-fielded-roster` Phase 1): `npcf_*` ids ride the SAME
  // `gatherPilotHandlers` vector as `talent`/`core_bonus`/`pilot_gear` (merged in by
  // `combatantEffectIds`), so the row is identical to theirs, PLUS `"trample"`
  // (Phase 5.1) — `resolveTrampleTrigger` (`resolve/triggers.ts`) gathers the SAME
  // `gatherPilotHandlers`/`combatantEffectIds` pool, so an `npcf_*` Trait (Trample) reaches
  // it exactly as it reaches `"action"`/`"start_turn"`. **Not added to the `talent`/`core_bonus`/
  // `pilot_gear`/`system` rows above** — no roster content on this project's scope carries a
  // `trample` clause off those carrier kinds, and this table is a calibration of REAL fire
  // sites reached, not a speculative ceiling; widen those rows too if a future project does.
  // `"move"`: the observed-move window (`observedMove.ts`) gathers a watcher's pilot vector (Rapid Response).
  npc_feature: ["attack", "save", "start_turn", "end_turn", "start_round", "end_round", "action", "trample", "damage", "move"],
};

// ── carrier-kind reachability for a MODIFIER clause's facets ─────────────────────────

/**
 * Which {@link ModifierContribution} facets a `modifier` clause carried by each {@link
 * ReachabilityKind} can ACTUALLY reach a real fold through — the modifier-clause sibling of
 * {@link REACHABLE_TRIGGER_POINTS} (same discipline: a hand-maintained calibration kept in
 * lock-step with the real gather sites in `resolve/attackModifiers.ts`/`resolve/toHit.ts`/`saves.ts`/`contest.ts`/`interpret.ts`,
 * pinned by a runtime test — `reachableModifierFacets.pin.test.ts` — never re-derived
 * automatically).
 *
 * **This is the shared source of truth for BOTH consumers — widening the fold updates
 * detection and coverage by construction, so they cannot drift:**
 *   - `classifyClause`'s modifier-kind reachability branch below (without it, a
 *     system-carried `modifier` reports `mechanized` while doing nothing).
 *   - `lint.ts`'s `ruleSystemModifierAttackFold`, which reads
 *     `REACHABLE_MODIFIER_FACETS.system`.
 *
 * **Derivation (traced from the real fold sites, not guessed):**
 *
 * - **`system`** — `seedSystemModifiers` is folded at FIVE real sites:
 *   `interpretForceSaveRoll`'s FORCED save (the pre-existing wired consumer), `saves.ts`'s
 *   `save_ends` turn-start re-save, `contest.ts`'s opposed-check `rollSide`, and `resolve/attackModifiers.ts`'s
 *   `atkMods`/`tgtMods` attack fold. Reachable: the save/check dimension
 *   (`saveCheckBonus`/`consumeOnSaveCheck`/`saveScope`, all three folds) and `autoFailChecks`
 *   (forced-save + contest + `saves.ts`'s `save_ends` re-save, the last scoped per instance by
 *   its own `expiry.stat` — `decisions/0163` D6, see `ModifierContribution.autoFailChecks`'s own
 *   doc); the full attack-fold facet set
 *   (`attackerAccuracy`/`overrideDamageType`/`advisory` via `atkMods`,
 *   `targetAccuracy`/`consumeOnHit`/`ignoreTargetArmor`/`doubleDamage`/`evasionCap`/
 *   `substituteStructureDamage` via `tgtMods` — the last added by
 *   `npc-features-fielded-roster` Phase 8.1, riding the SAME `tgtMods` fold as the other six with
 *   no new gather vector) and `consumeResourceOnApply` (its cap gate runs before the
 *   attacker/target split, so either side's fold reads it). **Reachable (Gap B,
 *   `npc-features-fielded-roster` Phase 2, `decisions/0171`):** `resistance` — `foldedResistance`
 *   (`seed.ts`) was widened off the narrow `foldSelf`/`gatherModifiers` (status-borne only) to the
 *   composed `foldWide`/`gatherModifiersAt` (`STATUS_VECTOR` + `PILOT_EFFECT_VECTOR` +
 *   `SYSTEM_VECTOR`), so a system-carried `modifier { resistance: … }` now folds through a real
 *   attack's mitigation (proven by `reachableModifierFacets.pin.test.ts`'s system-carried
 *   resistance case and the Point-Defense Shield entry test,
 *   `backend/src/npcFeatureResistanceFold.integration.test.ts`). The same fold also reaches the
 *   sourced NON-attack damage sites (bulldoze run-over, mine burst, Jockey Menu Damage, the
 *   `damage`/`extra_damage` atoms) through `nonAttackResistant` (`nonAttackResistance.ts`), and
 *   the unsourced ones (dangerous terrain, the burn tick, GM damage) fold it in no-attacker mode,
 *   where an attacker-relative grant reads false. Every carrier kind is affected alike, so the
 *   per-kind reachable set is unchanged. **Reachable (the
 *   `immunity-foldwide` widening, which finished what Gap B started):** `immunity` — the facet is
 *   ONE array read by FOUR predicates (`immuneToDamage`, `immuneToTechAction`,
 *   `statusBlockedByImmunity`, `hasBlanketImmunity`), and Gap B had widened only the first, so
 *   this row deliberately withheld the facet rather than over-claim for the `tech`/`status`-scoped
 *   and blanket cases (this table has no per-scope granularity to say "reachable for the damage
 *   scope only"). All four now read the SAME `foldWide`/`gatherModifiersAt` composition, so the
 *   whole facet is reachable for this carrier kind with no scope carve-out left to state —
 *   proven by `reachableModifierFacets.pin.test.ts`'s system-carried status-immunity case.
 *
 * - **`talent` / `core_bonus` / `pilot_gear`** — `seedPilotModifiers` is folded at
 *   all FIVE of the same real sites, on BOTH attack-fold sides (unlike a system, a pilot
 *   effect's carrier is threaded at `attacker.id` in `atkMods` AND `target.id` in `tgtMods`).
 *   So every facet `system`'s row reaches is reachable here too. `autoFailChecks` is read by `interpretForceSave`/`contest.ts`/
 *   `saves.ts`'s `save_ends` re-save, regardless of which vector feeds it — same as `system`'s
 *   row. Same **Gap B `resistance` widening as `system`** (`PILOT_EFFECT_VECTOR` rides the same
 *   `gatherModifiersAt` composition) — a talent/core-bonus/pilot-gear-carried
 *   `modifier { resistance: … }` now folds; the shipped carrier for a *talent* specifically is
 *   `pilotEffectIds` (the legitimate shape for that kind — see the pin test). Same **`immunity`
 *   widening** as `system`'s row (`immunity-foldwide`): all four `immunity` read-sites fold the
 *   same `gatherModifiersAt` composition now, so the facet is reachable here for the identical
 *   reason `resistance` is — `PILOT_EFFECT_VECTOR` is part of that composition.
 *
 * - **`tag`** — `seedWeaponModifiers`/`gatherWeaponModifiers` fold a weapon's TAG entries ONLY at
 *   the ATTACKER side of `atkMods` (a tag template has no "carrier" other than the
 *   wielder firing it — never gathered against `target.id` anywhere). Reachable: the
 *   attacker-side attack-fold facets (`attackerAccuracy`/`overrideDamageType`/
 *   `advisory`/`consumeResourceOnApply`). **NOT reachable:** every target-side facet
 *   (`targetAccuracy`/`consumeOnHit`/`ignoreTargetArmor`/`doubleDamage`/`evasionCap`/
 *   `substituteStructureDamage` — never gathered on the target side), the whole
 *   save/check dimension (`seedWeaponModifiers` is never folded into
 *   `interpretForceSaveRoll`/`saves.ts`/`contest.ts`), and `immunity`/`resistance`
 *   (`gatherWeaponModifiers` is not in `gatherModifiersAt`'s STATUS + PILOT-EFFECT + SYSTEM
 *   composition, and a tag has no self to be immune or resistant on).
 * - **`weapon`** — EMPTY. `gatherWeaponModifiers` walks `weapon.tags` and never looks up
 *   `overlay[weapon.id]`, so a `modifier` clause on an `mw_*` entry's own id folds nowhere
 *   (`prototype-3.test.ts`'s weapon-own `attackerAccuracy` arm measures it). A weapon's own
 *   `triggered` clauses are a different vector (`gatherHandlers`) and are unaffected.
 *
 * **`tag`/`spatialRiderDeferred` are deliberately absent from every row** — both are checked by
 * `classifyClause`'s OWN dedicated branches (the tag-rider recording and the deferred-spatial-
 * rider note) independent of carrier kind, so the modifier-reachability branch below excludes
 * them from its per-facet check entirely rather than risk double-flagging (or, worse, flipping
 * a `tg_*` tag-rider clause to `manual`).
 *
 * **`attackScope` rides `attackerAccuracy`'s row membership
 * exactly, in every row** — it's not a new gather vector, it's a pre-fold STRIP
 * (`modifiers.ts`'s `scopeAttackModifiers`) applied to the SAME combined attacker-side list
 * `resolve/attackModifiers.ts` already builds for `attackerAccuracy` (status + weapon/tag + pilot-effect +
 * system + conferred, all concatenated into ONE array before the strip runs) — so whatever
 * kind reaches `attackerAccuracy` through that fold reaches `attackScope` identically, by
 * construction, not by guessing. t_juggernaut:1 (`talent`) is the one shipped carrier;
 * `reachableModifierFacets.pin.test.ts` independently proves the `system` row's entry the
 * same way it proves `attackerAccuracy`'s. **What the strip REMOVES is a wider set than what
 * makes it reachable**: it drops the whole `ATTACK_SCOPED_FACETS` domain (every facet
 * attack resolution consumes). That does not move any row here — reachability is about which GATHER vectors feed the fold, and the
 * strip adds none.
 */
export const REACHABLE_MODIFIER_FACETS: Record<ReachabilityKind, readonly (keyof ModifierContribution)[]> = {
  weapon: ["ignoreDifficulty", "damageEqualsDistance", "reliableUnlessStructure", "attacksTargets", "lineToTarget", "usableWhileJammed", "sparesAllies", "areaOnHit"],
  tag: ["attackerAccuracy", "attackScope", "overrideDamageType", "armorPiercing", "bonusDamageType", "consumeResourceOnApply", "advisory"],
  talent: [
    "attackerAccuracy",
    "attackScope",
    "targetAccuracy",
    "consumeOnHit",
    "ignoreTargetArmor",
    "doubleDamage",
    "evasionCap",
    "substituteStructureDamage",
    "overrideDamageType",
    "ignoreCover",
    "armorPiercing",
    "bonusDamageType",
    "consumeResourceOnApply",
    "saveCheckBonus",
    "consumeOnSaveCheck",
    "saveScope",
    "autoFailChecks",
    "resistance",
    "immunity",
    "cannotAttack",
    "coverToAllies",
    "deniesSoftCover",
    "chooseInvoluntaryHeading",
    "rangeBonus",
    "minimumRange",
    "cannotMelee",
    "ramsLarger",
    "actionCost",
    "advisory",
  ],
  core_bonus: [
    "attackerAccuracy",
    "attackScope",
    "targetAccuracy",
    "consumeOnHit",
    "ignoreTargetArmor",
    "doubleDamage",
    "evasionCap",
    "substituteStructureDamage",
    "overrideDamageType",
    "ignoreCover",
    "armorPiercing",
    "bonusDamageType",
    "consumeResourceOnApply",
    "saveCheckBonus",
    "consumeOnSaveCheck",
    "saveScope",
    "autoFailChecks",
    "resistance",
    "immunity",
    "cannotAttack",
    "coverToAllies",
    "deniesSoftCover",
    "chooseInvoluntaryHeading",
    "rangeBonus",
    "minimumRange",
    "cannotMelee",
    "ramsLarger",
    "actionCost",
    "advisory",
  ],
  pilot_gear: [
    "attackerAccuracy",
    "attackScope",
    "targetAccuracy",
    "consumeOnHit",
    "ignoreTargetArmor",
    "doubleDamage",
    "evasionCap",
    "substituteStructureDamage",
    "overrideDamageType",
    "ignoreCover",
    "armorPiercing",
    "bonusDamageType",
    "consumeResourceOnApply",
    "saveCheckBonus",
    "consumeOnSaveCheck",
    "saveScope",
    "autoFailChecks",
    "resistance",
    "immunity",
    "cannotAttack",
    "coverToAllies",
    "deniesSoftCover",
    "chooseInvoluntaryHeading",
    "rangeBonus",
    "minimumRange",
    "cannotMelee",
    "ramsLarger",
    "actionCost",
    "advisory",
  ],
  // `resistance` added Gap B (`npc-features-fielded-roster` Phase 2, `decisions/0171`) —
  // `foldedResistance` widened off `foldSelf` to `foldWide`/`gatherModifiersAt`, which folds
  // `SYSTEM_VECTOR`. `immunity` added by the `immunity-foldwide` widening — it stayed OFF this
  // row while only `immuneToDamage` read the wide fold (a `tech`/`status`-scoped or blanket grant
  // would have been over-claimed); `immuneToTechAction`/`statusBlockedByImmunity`/
  // `hasBlanketImmunity` now read the same `foldWide`, so all four scopes are honestly reachable.
  system: [
    "attackerAccuracy",
    "attackScope",
    "targetAccuracy",
    "consumeOnHit",
    "ignoreTargetArmor",
    "doubleDamage",
    "evasionCap",
    "substituteStructureDamage",
    "overrideDamageType",
    "ignoreCover",
    "consumeResourceOnApply",
    "saveCheckBonus",
    "consumeOnSaveCheck",
    "saveScope",
    "autoFailChecks",
    "resistance",
    "immunity",
    "cannotAttack",
    "coverToAllies",
    "deniesSoftCover",
    "chooseInvoluntaryHeading",
    "rangeBonus",
    "minimumRange",
    "cannotMelee",
    "ramsLarger",
    "actionCost",
    "advisory",
  ],
  // Gap A (`npc-features-fielded-roster` Phase 1): `npcf_*` ids ride the SAME
  // `seedPilotModifiers` vector as `talent`/`core_bonus`/`pilot_gear` (merged in by
  // `combatantEffectIds`), so this row is IDENTICAL to theirs, regardless of which of the three
  // lists an id came from. **`resistance` added Gap B**
  // (`npc-features-fielded-roster` Phase 2, `decisions/0171`) — `foldedResistance` widened to
  // `gatherModifiersAt`, which folds `PILOT_EFFECT_VECTOR` (where `npcFeatureEffectIds` rides).
  // Point-Defense Shield/Stampede Defense/Siege Armor's `modifier { resistance: … }` clauses now
  // reach a real fold. **`immunity` added by the `immunity-foldwide` widening** — same story as
  // `system`'s row: the facet was withheld while only `immuneToDamage` read the wide fold, and
  // `immuneToTechAction`/`statusBlockedByImmunity`/`hasBlanketImmunity` now read it too, so the
  // whole facet is reachable with no scope carve-out. Aegis's Regenerative Shielding
  // (`npcf_regenerative_shielding_aegis`, Trait → `npcFeatureEffectIds` → `PILOT_EFFECT_VECTOR`)
  // is the fielded consumer: `modifier { immunity: [{kind:"status", name:"Impaired"|"Slowed"}] }`
  // reaching `statusBlockedByImmunity`, which was `manual` here before this change.
  npc_feature: [
    "attackerAccuracy",
    "attackScope",
    "targetAccuracy",
    "consumeOnHit",
    "ignoreTargetArmor",
    "doubleDamage",
    "evasionCap",
    "substituteStructureDamage",
    "overrideDamageType",
    "ignoreCover",
    "armorPiercing",
    "bonusDamageType",
    "consumeResourceOnApply",
    "saveCheckBonus",
    "consumeOnSaveCheck",
    "saveScope",
    "autoFailChecks",
    "resistance",
    "immunity",
    "cannotAttack",
    "coverToAllies",
    "deniesSoftCover",
    "chooseInvoluntaryHeading",
    "rangeBonus",
    "minimumRange",
    "cannotMelee",
    "ramsLarger",
    "actionCost",
    "advisory",
  ],
};

// ── actor-ref bound-roles ────────────────────────────────────────────────────────────

/**
 * How a `self`/`target`/`attacker`/`partner` ref actually resolves for a `triggered`
 * clause firing at a given point — the calibration the authoring lint's `error`/`warn`
 * severities read.
 * Four states, not a bare boolean, because "resolves" isn't the whole story — a ref can
 * resolve to something REAL but WRONG, and that's the case worth a lint finding at all:
 *
 *   - `"bound"` — resolves to the genuinely-named, intentional combatant (a real
 *     distinct target/attacker/compelled actor, or `self` which is always `h.source`).
 *     No finding.
 *   - `"aliased_carrier"` — resolves, but SILENTLY to the firing clause's own carrier
 *     (`h.source`) — a real id, so the closed language never crashes, but almost never
 *     what an author who wrote `target`/`attacker` meant. `warn` at the site that
 *     documents this (`triggerBus.ts`'s `fireTrigger`, `resolve.ts`'s `resolveActivate`).
 *   - `"ambiguous"` — resolves to something real, but WHICH real value (a genuine
 *     caller-supplied one vs. a silent alias) isn't statically knowable from the clause
 *     alone (`resolveActivate`'s `target: intent.target ?? actor.id` — whether the
 *     caller's `intent.target` was supplied is a runtime fact, not an authoring one). No
 *     finding — flagging it would be a false positive on every well-formed clause that
 *     uses the designed caller-supplied binding.
 *   - `"unbound"` — never resolves (`undefined`) — `resolveActorId` returns nothing, the
 *     closed predicate evaluator reads it as `false`, the atom interpreter skips the
 *     recipient. `error`: a clause naming this ref at this point is provably dead
 *     (see `predicate.ts`'s `resolveActorId` doc).
 *
 * `partner` is `"unbound"` everywhere in this table: `EffectContext.actors` (`interpret.
 * ts`) carries no `partner` field at all — only `PredicateContext.actors` (`predicate.
 * ts`) does, populated per-clause by `foldModifiers`/the protector-gather compiler for
 * `modifier` clauses specifically, which this table (keyed on `TriggerFamily`, the
 * `triggered`-clause vocabulary) doesn't cover.
 */
export type ActorBinding = "bound" | "aliased_carrier" | "ambiguous" | "unbound";

/** The `self`/`target`/`attacker`/`partner` binding profile for one firing point. */
export type BoundRoleProfile = {
  self: ActorBinding;
  target: ActorBinding;
  attacker: ActorBinding;
  partner: ActorBinding;
};

/**
 * The bound-roles profile for every {@link TriggerFamily} — a **hand-maintained
 * calibration**, in the same class as {@link REACHABLE_TRIGGER_POINTS} (kept in
 * lock-step with the real fire sites in the `resolve/` modules/`interpret.ts`/
 * `reactionRegistry.ts`, never re-derived automatically). Pinned by a **runtime** test
 * (`actorBindingReach.pin.test.ts`) that drives the real production fire path per family
 * and records which roles actually resolve, and to what id — not a source grep, which
 * can't work here: the family is a string literal at some `fireTrigger` call sites but
 * threaded through a `fireCtx` variable at others. **A new fire site that binds `attacker`
 * at a new family (or changes what `target` aliases to) must update this table, or the
 * lint reports a false verdict at that family.**
 *
 * Per-family notes:
 *   - **`attack`** — `fireAttackDeclared`/the on_hit·on_crit·on_miss fires all pass a
 *     real `attacker` and a real `primaryTarget` (the actual target, not the carrier).
 *     `self`/`target`/`attacker` are three genuinely distinct, intentional bindings.
 *   - **`save`** — `fireForcedSaveDeclared` (`interpret.ts`) passes `attacker: selfId`
 *     (the FORCING combatant's own id, from the outer `EffectContext.actors.self` — the
 *     one whose `force_save` atom compelled the roll) and `primaryTarget: actor.id` (the
 *     COMPELLED combatant, resolved from the atom's own `actor` ref against that SAME
 *     outer context). Both are real, independently-resolved ids — `target` does **not**
 *     alias to the save-clause's own carrier the way the lifecycle families below do
 *     (proven empirically: `actorBindingReach.pin.test.ts`'s save case uses three
 *     distinct combatants — carrier, forcer, compelled — and the marker lands on the
 *     right one for each ref). "target is always aliased to the carrier outside attack"
 *     describes the *lifecycle* families' fire sites specifically (which hardcode
 *     `primaryTarget: carrierId` in their own source), not `save`'s.
 *   - **`damage`** — consumed by TWO sites (`decisions/0175`). Non-attack damage fires it on
 *     the bus (`resolveDamageTrigger`: `self === target ===` the damaged combatant, `attacker`
 *     unbound — runtime-pinned by `actorBindingReach.pin.test.ts`). Attack damage consumes it
 *     through `reactionRegistry.ts`'s three compilers
 *     (`compileAuthoredOffers`/`compileAutomaticOutcome`/`compileProtectorOffers`,
 *     `REGISTRY_CONSUMED_TRIGGER_POINTS`), each of which builds its own
 *     `EffectContext.actors` by hand rather than going through `fireTrigger`. Two of the
 *     three (`compileAuthoredOffers`, `compileAutomaticOutcome`) bind `self === target ===
 *     the damaged/reacting combatant` and `attacker === the triggering attack's
 *     attacker` — a real, distinct `attacker` (matching the corpus's one shipped
 *     `attacker` ref on a `damage` clause, Covering Fire Watcher). The third
 *     (`compileProtectorOffers`) diverges further: its *condition*-gate context binds
 *     `self = the protector` (a THIRD combatant, distinct from the damaged one) while its
 *     *effect*-interpretation context collapses `self === target === the protector`
 *     instead. This row states the DOMINANT (2-of-3-compiler) shape, and its `attacker`
 *     cell is `ambiguous` because the bus site binds none; the attack-window half is
 *     traced-from-source, not runtime-proven. Anyone adding a `damage`-family
 *     lint rule should re-derive this from `reactionRegistry.ts` directly rather than
 *     trusting this row at face value.
 *   - **`status`/`turn`/`round`/`move`** (the lifecycle families) — every fire
 *     site (`resolveStatusLostHandlers`, `resolveLifecycleTrigger`,
 *     `resolveMoveTrigger`, all `resolve.ts`) passes
 *     `primaryTarget: carrierId` (the SAME id as the firing clause's own carrier) and
 *     never sets `fire.attacker` at all. So `target` silently aliases to `self`
 *     (`aliased_carrier`) and `attacker` is `unbound` — structurally, not just unused:
 *     there is no fallback to catch it.
 *   - **`action`** — `attacker` is `unbound` for the same reason as the lifecycle
 *     families above, but `target` is **`ambiguous`**. `resolveActionTakenTrigger`
 *     takes an optional `primaryTarget` (defaulting to `carrierId`), and its two
 *     production callers bind it differently: `refereeIntent`/`refereeCommand` omit it (the
 *     alias — `boost`, `overcharge`, `prepare`, every action a combatant takes *to
 *     itself*), while `refereeSharedGateAction`'s dismount arm passes the just-spawned
 *     on-foot pilot, a genuinely distinct combatant. **Dismount is structurally different
 *     from every other action on this bus: its whole effect is to mint a SECOND combatant**,
 *     so "the carrier is the target" was never going to hold for it. Which binding a clause
 *     gets is therefore a runtime fact about the fired action, not something readable off the
 *     clause — the definition of `ambiguous`, and the same reason `resolveActivate`'s
 *     `target: intent.target ?? actor.id` carries that arm. The cost is real and worth
 *     stating: the `target-aliased` warn no longer fires for an action-family clause, so an
 *     author who writes `target` meaning `self` on a `boost` clause is no longer nudged. That
 *     is the lesser error — the alternative keeps the table asserting something false and
 *     actively teaches an author of a dismount-matched clause to write `self`, which would
 *     land the effect on the mech instead of the pilot. `t_black_thumb:1` is the shipped
 *     carrier of the distinct binding.
 *   - **`heat`/`track_damage`/`destruction`** — **INERT**: no fire site anywhere
 *     consumes these (`inertTriggers.ts`'s `inertTriggeredClauses`, keyed on the
 *     complement of `FIRED_TRIGGER_POINTS ∪ REGISTRY_CONSUMED_TRIGGER_POINTS`, already
 *     catches any clause authored at one of these). Every role reads `"unbound"` here
 *     for `Record<TriggerFamily, …>` completeness, not because a real binding was traced
 *     — nothing ever fires, so nothing ever resolves.
 */
export const FAMILY_BOUND_ROLES: Record<TriggerFamily, BoundRoleProfile> = {
  attack: { self: "bound", target: "bound", attacker: "bound", partner: "unbound" },
  save: { self: "bound", target: "bound", attacker: "bound", partner: "unbound" },
  // `attacker` is `ambiguous`: bound to the real attacker at the attack interception window,
  // unbound at the non-attack bus site (`resolveDamageTrigger`) — which one a clause gets depends
  // on what damaged the carrier, not on the clause.
  damage: { self: "bound", target: "bound", attacker: "ambiguous", partner: "unbound" },
  status: { self: "bound", target: "aliased_carrier", attacker: "unbound", partner: "unbound" },
  start_turn: { self: "bound", target: "aliased_carrier", attacker: "unbound", partner: "unbound" },
  end_turn: { self: "bound", target: "aliased_carrier", attacker: "unbound", partner: "unbound" },
  start_round: { self: "bound", target: "aliased_carrier", attacker: "unbound", partner: "unbound" },
  end_round: { self: "bound", target: "aliased_carrier", attacker: "unbound", partner: "unbound" },
  // `action`'s `target` is **`ambiguous`, not `aliased_carrier`** — see this table's own doc
  // block above for the full note. There are
  // TWO production callers of `resolveActionTakenTrigger` and they bind `target` differently:
  // `refereeIntent`/`refereeCommand` omit `primaryTarget` (so it defaults to `carrierId`, the
  // alias), while `refereeSharedGateAction`'s dismount arm passes the just-spawned pilot — a
  // genuinely distinct combatant. Which one a given clause gets is a runtime fact about the
  // action, not an authoring fact readable off the clause, which is exactly what `ambiguous` is
  // for (`resolveActivate`'s `intent.target ?? actor.id` is the precedent arm).
  action: { self: "bound", target: "ambiguous", attacker: "unbound", partner: "unbound" },
  move: { self: "bound", target: "aliased_carrier", attacker: "unbound", partner: "unbound" },
  // `trample`'s `target` is genuinely BOUND, not `aliased_carrier` — unlike every other
  // non-attack family's "the carrier is the target" default, `resolveTrampleTrigger` passes
  // `primaryTarget` as the CROSSED character, a real, distinct combatant from the carrying
  // mover (`self`). Mirrors `attack`'s shape (self/target both genuinely bound, distinct
  // actors) rather than `turn`/`round`/`status`/`move`'s carrier-aliased shape.
  trample: { self: "bound", target: "bound", attacker: "unbound", partner: "unbound" },
  // `aura`: `self` is the drone's deployer and `target` the character in its field
  // (`resolveAuraTrigger`), two distinct combatants — `trample`'s shape.
  aura: { self: "bound", target: "bound", attacker: "unbound", partner: "unbound" },
  // `heat`/`track_damage`: the referee's track observer fires the carrier's own clauses with the
  // carrier as the target (`referee/trackObserver.ts`) — `status`'s shape.
  heat: { self: "bound", target: "aliased_carrier", attacker: "unbound", partner: "unbound" },
  track_damage: { self: "bound", target: "aliased_carrier", attacker: "unbound", partner: "unbound" },
  destruction: { self: "unbound", target: "unbound", attacker: "unbound", partner: "unbound" },
};

/**
 * The bound-roles profile for a `kind: "action"` clause — **NOT a {@link TriggerFamily}
 * row**, and deliberately kept out of {@link FAMILY_BOUND_ROLES}. An `action`-kind
 * clause (a player-activated ability) has no `on` family at all and is never gathered
 * by `fireTrigger`/`gatherHandlersAt` — it's bound entirely by `resolveActivate`
 * (`resolve.ts`), on a completely separate path: `actors: { self: actor.id, attacker:
 * actor.id, target: intent.target ?? actor.id }`.
 *
 *   - `self` — the activating actor. Always bound.
 *   - `attacker` — **bound, but SILENTLY aliased to the activator** (`actor.id`, the
 *     SAME value as `self`) — there is no "attacker" concept at an activation at all,
 *     `resolveActivate` just reuses the activator's own id. A clause naming `attacker`
 *     here reads real, not `undefined`, so `error` would be a lie — `warn` ("resolves to
 *     the activator — say `self` if that's the intent"), the same shape as a lifecycle
 *     `target` warn. Zero corpus hits today.
 *   - `target` — **`ambiguous`**: `intent.target` is the caller-supplied binding
 *     (`RequestActivate.target`, e.g. Overpower Caliber's extra strike naming another
 *     combatant) when the player's intent supplies one, and silently falls back to the
 *     activator's own id (`actor.id`) when it doesn't (a self-only action, or a targeted
 *     action activated without a target). Which case applies for a given authored clause
 *     is NOT statically knowable — it depends on how the ability is invoked at the
 *     table, not on the clause's own text. Flagging this would produce a false positive
 *     on every `target`-referencing action clause (42 corpus hits) and destroy the
 *     tool's credibility. No finding.
 *   - `partner` — unbound; `resolveActivate`'s `EffectContext.actors` carries no
 *     `partner` field either.
 */
/**
 * The bound roles of an observed-move clause (`on: "move"`, `bystander: true`, `observedMove.ts`):
 * `self` is the watcher and `target` the mover, two distinct combatants, unlike the mover's own
 * `move` fire where `target` aliases the carrier.
 */
export const OBSERVED_MOVE_BOUND_ROLES: BoundRoleProfile = { self: "bound", target: "bound", attacker: "unbound", partner: "unbound" };

export const ACTIVATE_BOUND_ROLES: BoundRoleProfile = {
  self: "bound",
  target: "ambiguous",
  attacker: "aliased_carrier",
  partner: "unbound",
};

// ── per-clause classification ────────────────────────────────────────────────────────

/**
 * Classify one {@link EffectEntry} clause into a coverage tier with its reasons. Pure
 * — reads only the clause + the static vocabularies. The rules:
 *   - any **unimplemented** atom op or predicate op → `manual` (the cheap gap column; the
 *     engine cannot run the clause without a new `case`);
 *   - else any **deferred** facet (a `gm_ruling` predicate, an `advisory` atom, a tag rider
 *     named in {@link UNMET_STEP_DEMANDS}, a deferred spatial rider, the empty `builtin` hatch) →
 *     `advisory` (runs partially; the facet routes to the GM and is reported). An `action`
 *     clause's `activation` is not a deferred facet: `economy/cost.ts` prices and gates it;
 *   - else → `mechanized` (the data path runs it end-to-end).
 */
export function classifyClause(entryId: string, clauseIdx: number, clause: EffectEntry): ClauseCoverage {
  const reasons: string[] = [];
  let unmet = false;
  let advisory = false;

  // 1. unimplemented atom / predicate ops → manual (cheap gap column).
  const atomOps = new Set<string>();
  const predOps = new Set<string>();
  const atoms: readonly EffectAtom[] = clause.kind === "modifier" || clause.kind === "select" || clause.kind === "replacement" ? [] : clause.effect;
  collectAtomOps(atoms, atomOps);
  collectPredicateOps(atoms, predOps);
  if (clause.kind === "modifier" && clause.condition) collectPredOps(clause.condition, predOps);
  if (clause.kind === "replacement" && clause.on === "roll" && clause.condition) collectPredOps(clause.condition, predOps);
  if ((clause.kind === "triggered" || clause.kind === "action") && clause.condition) {
    collectPredOps(clause.condition, predOps);
  }
  if (clause.kind === "invade_option" && clause.when) collectPredOps(clause.when, predOps);
  for (const op of atomOps) {
    if (!IMPL_ATOM.has(op)) {
      unmet = true;
      reasons.push(`unmet atom op "${op}" (no interpreter case — cheap gap column)`);
    }
  }
  for (const op of predOps) {
    if (!IMPL_PRED.has(op)) {
      unmet = true;
      reasons.push(`unmet predicate op "${op}" (no evaluator case — cheap gap column)`);
    }
  }

  // 2. deferred facets → advisory.
  // 2a. a GM ruling in the loop (`gm_ruling`, `decisions/0183`).
  const gmRuling =
    usesGmRuling(atoms) ||
    (clause.kind === "modifier" && clause.condition ? predUsesGmRuling(clause.condition) : false) ||
    ((clause.kind === "triggered" || clause.kind === "action") && clause.condition
      ? predUsesGmRuling(clause.condition)
      : false) ||
    (clause.kind === "invade_option" && clause.when ? predUsesGmRuling(clause.when) : false);
  if (gmRuling) {
    advisory = true;
    reasons.push("a GM ruling is in the loop (gm_ruling: the GM answers a fact the engine does not model)");
  }
  // 2a'. a GM-routed `advisory` note anywhere in the effect tree: the GM still rules on what it says.
  for (const message of advisoryMessages(atoms)) {
    advisory = true;
    const note = message.length > 100 ? `${message.slice(0, 100)}…` : message;
    reasons.push(`GM-routed advisory note: "${note}"`);
  }
  // 2b. a tag rider. One named in UNMET_STEP_DEMANDS defers a new grounded step → advisory; a
  // recorded rider's mechanism runs off the frozen weapon profile, so it stays mechanized and
  // only says why — unless the clause's own `advisory` note names a part of the tag the engine
  // leaves to the GM (Thrown's retrieval).
  if (clause.kind === "modifier" && clause.mod.tag) {
    const name = clause.mod.tag.name;
    const demand = UNMET_STEP_DEMANDS[name];
    if (demand) {
      advisory = true;
      reasons.push(`tag rider "${name}" recorded but defers the "${demand.step}" grounded step (expensive gap column)`);
    } else if (TAG_RIDER.has(name) && clause.mod.advisory !== undefined) {
      advisory = true;
      reasons.push(`tag rider "${name}" recorded; the engine runs its profile mechanism, but the clause's advisory names a GM-enforced remainder`);
    } else if (name === "knockback") {
      reasons.push(`tag rider "knockback" recorded; the positional push is mechanized by a sibling on-hit \`forced_move\` clause`);
    } else if (TAG_RIDER.has(name)) {
      reasons.push(`tag rider "${name}" recorded; the engine runs its mechanism off the frozen weapon profile (A2 flatten)`);
    }
  }
  // 2b'. a modifier carrying a deferred spatial rider (Prone's adjacency math) — the
  // advisory part (the warning) mechanizes, but the spatial rider is NOTED-not-implemented
  // (downstream of the mapping arc), so the clause defers honestly → advisory.
  if (clause.kind === "modifier" && clause.mod.spatialRiderDeferred) {
    advisory = true;
    reasons.push("carries a deferred spatial rider (D10 — adjacency math needs the map; the advisory surfaces, the rider is noted-not-implemented)");
  }
  // 2c. the empty builtin hatch.
  if (atomOps.has("builtin")) {
    advisory = true;
    reasons.push("references the `builtin` first-party hatch (registry empty in v1 — surfaced, GM-resolved)");
  }
  // 2e. reachability — a `triggered` clause whose carrier KIND
  // (confidently inferred from its OWN id, never guessed — see `reachabilityKindOf`) has no
  // gather vector wired at its trigger point is UNREACHABLE for a real combatant of that kind,
  // no matter how faithfully the atom/predicate vocabulary is implemented. This is the
  // false-clear class a fixture-hack entry test produces: the entry test proves the atom works while
  // no real spawned combatant of that kind can reach the clause at all. Routes to `manual` — the
  // engine cannot run this clause for a REAL combatant of this kind at all, the same practical
  // consequence (GM hand-adjudicates) as an unimplemented op, just a reachability gap instead
  // of an implementation gap.
  if (clause.kind === "triggered") {
    const rkind = reachabilityKindOf(entryId);
    if (rkind !== undefined && !REACHABLE_TRIGGER_POINTS[rkind].includes(clause.on)) {
      unmet = true;
      reasons.push(
        `carrier kind "${rkind}" (inferred from id) has no gather vector wired at trigger family "${clause.on}" — unreachable for a real combatant of this kind (reachability gap, not an implementation gap; LL0 DSL Primitives IV P1(c))`,
      );
    }
  }
  // 2f. reachability for a `modifier` clause — the modifier-kind sibling of 2e above. Without
  // it a system-carried `modifier` (`ms_scanner_swarm`'s intended attackerAccuracy grant)
  // reports `mechanized` while its facet never reaches a real fold. `tag`/`spatialRiderDeferred` are
  // excluded from this check (2b/2b' above already classify them honestly, independent of
  // carrier kind — see `REACHABLE_MODIFIER_FACETS`'s own doc for why re-checking them here
  // would risk flipping a `tg_*` tag-rider clause to `manual`).
  if (clause.kind === "modifier") {
    const rkind = reachabilityKindOf(entryId);
    if (rkind !== undefined) {
      const reachable = REACHABLE_MODIFIER_FACETS[rkind];
      const offending = (Object.keys(clause.mod) as (keyof ModifierContribution)[]).filter(
        (k) => k !== "tag" && k !== "spatialRiderDeferred" && !reachable.includes(k),
      );
      if (offending.length > 0) {
        unmet = true;
        reasons.push(
          `carrier kind "${rkind}" (inferred from id) has no gather vector feeding modifier facet(s) ${offending.map((f) => `"${String(f)}"`).join(", ")} into a real fold — unreachable for a real combatant of this kind (reachability gap, not an implementation gap; effects-gather-and-legality-defects D6/defect D)`,
        );
      }
    }
  }

  const tier: CoverageTier = unmet ? "manual" : advisory ? "advisory" : "mechanized";
  if (tier === "mechanized") reasons.push("fully mechanized — every op folds into a grounded step or the stat/roll math");
  return { entry: entryId, clause: clauseIdx, kind: clause.kind, tier, reasons };
}

// ── the aggregate report ─────────────────────────────────────────────────────────────

/**
 * Build the aggregate {@link CoverageReport} over an overlay's authored entries — the
 * load-bearing deliverable. Walks every clause, classifies it, rolls up the tier tally, and
 * accumulates the two gap columns (unmet atoms by demand; unmet steps by demand). Pure +
 * deterministic — the gap columns are sorted by descending demand then op (stable for diffs).
 *
 * Takes either the seed entries (`SEED_ENTRIES`) or the whole compendium overlay — the
 * analysis is identical. This is what `lvtt overlay coverage` renders.
 */
export function coverageReport(entries: readonly OverlayEntry[]): CoverageReport {
  const perClause: ClauseCoverage[] = [];
  const tierCounts: Record<CoverageTier, number> = { mechanized: 0, advisory: 0, manual: 0 };
  const unmetAtomMap = new Map<string, UnmetAtom>();
  const unmetStepMap = new Map<string, UnmetStep>();
  // Every op's raw reference count, implemented or not (the unmet-atom map above
  // only ever holds the UNimplemented ones) — keyed the same `"kind:op"` way so a lookup
  // against IMPLEMENTED_ATOM_OPS/IMPLEMENTED_PREDICATE_OPS below is a plain Map.get.
  const usageCounts = new Map<string, number>();

  for (const entry of entries) {
    entry.effects.forEach((clause, i) => {
      const cov = classifyClause(entry.id, i, clause);
      perClause.push(cov);
      tierCounts[cov.tier] += 1;

      // unmet-atom column: collect the unimplemented atom/predicate ops this clause used.
      const atoms: readonly EffectAtom[] = clause.kind === "modifier" || clause.kind === "select" || clause.kind === "replacement" ? [] : clause.effect;
      const atomOps = new Set<string>();
      const predOps = new Set<string>();
      collectAtomOps(atoms, atomOps);
      collectPredicateOps(atoms, predOps);
      if (clause.kind === "modifier" && clause.condition) collectPredOps(clause.condition, predOps);
      if ((clause.kind === "triggered" || clause.kind === "action") && clause.condition) {
        collectPredOps(clause.condition, predOps);
      }
      if (clause.kind === "invade_option" && clause.when) collectPredOps(clause.when, predOps);
      for (const op of atomOps) {
        if (!IMPL_ATOM.has(op)) bumpAtom({ map: unmetAtomMap, kind: "atom", op, entryId: entry.id });
        usageCounts.set(`atom:${op}`, (usageCounts.get(`atom:${op}`) ?? 0) + 1);
      }
      for (const op of predOps) {
        if (!IMPL_PRED.has(op)) bumpAtom({ map: unmetAtomMap, kind: "predicate", op, entryId: entry.id });
        usageCounts.set(`predicate:${op}`, (usageCounts.get(`predicate:${op}`) ?? 0) + 1);
      }

      // unmet-step column: a deferred facet that names a new grounded step.
      if (clause.kind === "modifier" && clause.mod.tag) {
        const demand = UNMET_STEP_DEMANDS[clause.mod.tag.name];
        if (demand) bumpStep({ map: unmetStepMap, facet: clause.mod.tag.name, demand, entryId: entry.id });
      }
    });
  }

  const opUsage: OpUsage[] = [
    ...IMPLEMENTED_ATOM_OPS.map((op): OpUsage => ({ kind: "atom", op, count: usageCounts.get(`atom:${op}`) ?? 0 })),
    ...IMPLEMENTED_PREDICATE_OPS.map((op): OpUsage => ({ kind: "predicate", op, count: usageCounts.get(`predicate:${op}`) ?? 0 })),
  ];

  return {
    entries: entries.length,
    clauses: perClause.length,
    tierCounts,
    perClause,
    unmetAtoms: [...unmetAtomMap.values()].sort(byDemand),
    unmetSteps: [...unmetStepMap.values()].sort(byStepDemand),
    opUsage,
  };
}

type BumpAtomArgs = {
  map: Map<string, UnmetAtom>;
  kind: UnmetAtom["kind"];
  op: string;
  entryId: string;
};

function bumpAtom(args: BumpAtomArgs): void {
  const { map, kind, op, entryId } = args;
  const key = `${kind}:${op}`;
  const cur = map.get(key);
  if (cur) {
    cur.count += 1;
    if (!cur.entries.includes(entryId)) cur.entries.push(entryId);
  } else {
    map.set(key, { kind, op, count: 1, entries: [entryId] });
  }
}

type BumpStepArgs = {
  map: Map<string, UnmetStep>;
  facet: string;
  demand: { step: string; why: string };
  entryId: string;
};

function bumpStep(args: BumpStepArgs): void {
  const { map, facet, demand, entryId } = args;
  const cur = map.get(facet);
  if (cur) {
    cur.count += 1;
    if (!cur.entries.includes(entryId)) cur.entries.push(entryId);
  } else {
    map.set(facet, { facet, step: demand.step, why: demand.why, count: 1, entries: [entryId] });
  }
}

function byDemand(a: UnmetAtom, b: UnmetAtom): number {
  return b.count - a.count || a.op.localeCompare(b.op);
}
function byStepDemand(a: UnmetStep, b: UnmetStep): number {
  return b.count - a.count || a.step.localeCompare(b.step);
}

// ── human-readable rendering (the CLI surface uses this; JSON callers use the struct) ──

/**
 * Render a {@link CoverageReport} as a compact human-readable summary — the tier tally, then
 * the **two gap columns** side by side (unmet atoms | unmet steps), then the **per-op usage
 * tally**, which always surfaces the zero-use ops
 * (a vocabulary word nothing exercises, self-announcing instead of reading as silently
 * "covered"); `--verbose` also prints the full per-op tally (every implemented op's count,
 * not just the zero ones). The `lvtt overlay coverage` verb prints this; `--json` emits the
 * struct instead. `--verbose` additionally adds the per-clause breakdown.
 */
export function renderCoverage(report: CoverageReport, opts: { verbose?: boolean } = {}): string {
  const lines: string[] = [];
  const { mechanized, advisory, manual } = report.tierCounts;
  lines.push(`Coverage over ${report.entries} entries / ${report.clauses} clauses:`);
  lines.push(`  mechanized: ${mechanized}   advisory: ${advisory}   manual: ${manual}`);
  lines.push("");
  lines.push("Two-column gap report (the authoring-harness backlog):");
  lines.push(`  UNMET ATOMS (cheap — a new interpreter/evaluator case):`);
  if (report.unmetAtoms.length === 0) lines.push("    (none — every authored op is implemented)");
  for (const u of report.unmetAtoms) {
    lines.push(`    ${u.kind} "${u.op}" ×${u.count}  [${u.entries.join(", ")}]`);
  }
  lines.push(`  UNMET STEPS (expensive — a new grounded primitive + inverse + round-trip):`);
  if (report.unmetSteps.length === 0) lines.push("    (none — every clause folds into the calibrated step set)");
  for (const u of report.unmetSteps) {
    lines.push(`    ${u.step} ←  ${u.facet} ×${u.count}  [${u.entries.join(", ")}]`);
    lines.push(`      ${u.why}`);
  }
  lines.push("");
  // The per-op usage tally: the OPPOSITE
  // direction from the unmet-atom column above (implemented ops, not unimplemented ones),
  // so a zero-carrier word in the implemented vocabulary self-announces instead of reading
  // as silently "covered."
  const zeroUse = report.opUsage.filter((u) => u.count === 0);
  lines.push(`Per-op usage tally (${report.opUsage.length} implemented ops; zero-use ones self-announce):`);
  if (zeroUse.length === 0) {
    lines.push("  ZERO-USE: (none — every implemented op is referenced at least once)");
  } else {
    lines.push(`  ZERO-USE (${zeroUse.length}):`);
    for (const u of zeroUse) lines.push(`    ${u.kind} "${u.op}" — 0 uses`);
  }
  if (opts.verbose) {
    lines.push("  full tally:");
    for (const u of report.opUsage) lines.push(`    ${u.kind} "${u.op}" ×${u.count}`);
    lines.push("");
    lines.push("Per-clause:");
    for (const c of report.perClause) {
      lines.push(`  [${c.tier}] ${c.entry}#${c.clause} (${c.kind})`);
      for (const r of c.reasons) lines.push(`      - ${r}`);
    }
  }
  return lines.join("\n");
}
